const MAX_OPERATING_SAVINGS_COLUMN = 'Savings (Max Operating Fleet)';
const MAX_OPERATING_ICE_SPENDING_COLUMN = 'Max Mileage Range ICE Spending Of Operating Vehicles';
const MAX_OPERATING_EV_SPENDING_COLUMN = 'Max Mileage Range EV Spending Of Operating Vehicles';
const ACTUAL_SAVINGS_COLUMN = 'Savings (Actual)';
const OPERATING_VEHICLES_COLUMN = 'Number of operating vehicles';
const DEFAULT_OPPORTUNITY_COPY = {
    impact: {
        actualSavings: 'Actual savings',
        maxOperatingSavings: 'Max operating fleet savings',
        additionalOpportunity: 'Additional opportunity',
        operatingVehiclesBasis: 'Operating vehicles basis',
        notAvailable: 'Not available',
        opportunityUnavailable: 'Max operating fleet data is not available for this period.',
    },
};

const compactWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const normalizeColumnLabelKey = (value) => compactWhitespace(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const parseNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const text = compactWhitespace(value).replace(/,/g, '');
    if (!text) return null;
    const numericValue = Number(text);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const getResultColumn = (result, targetColumn) => {
    const targetKey = normalizeColumnLabelKey(targetColumn);
    return (result?.columns || []).find((column) => normalizeColumnLabelKey(column) === targetKey) || '';
};

const sumResultColumn = (result, targetColumn) => {
    const column = getResultColumn(result, targetColumn);
    if (!column) return null;

    let total = 0;
    let hasNumericValue = false;
    (result?.rows || []).forEach((row) => {
        const numericValue = parseNumber(row?.[column]);
        if (numericValue === null) return;
        total += numericValue;
        hasNumericValue = true;
    });

    return hasNumericValue ? { column, value: total } : null;
};

const fallbackFormatCellValue = (column, value) => {
    if (value === null || value === undefined || value === '') return '--';
    return String(value);
};

const getMaxOperatingSavingsSummary = (result) => {
    const maxOperatingSavings = sumResultColumn(result, MAX_OPERATING_SAVINGS_COLUMN);
    if (maxOperatingSavings) return maxOperatingSavings;

    const maxOperatingIceSpending = sumResultColumn(result, MAX_OPERATING_ICE_SPENDING_COLUMN);
    const maxOperatingEvSpending = sumResultColumn(result, MAX_OPERATING_EV_SPENDING_COLUMN);
    if (!maxOperatingIceSpending || !maxOperatingEvSpending) return null;

    return {
        column: MAX_OPERATING_SAVINGS_COLUMN,
        value: maxOperatingIceSpending.value - maxOperatingEvSpending.value,
    };
};

const getStructuredOpportunityMetricValue = (metric) => {
    if (metric && typeof metric === 'object' && !Array.isArray(metric)) {
        return metric.value;
    }
    return metric;
};

const getStructuredOpportunityMetricColumn = (metric, fallbackColumn) => (
    metric && typeof metric === 'object' && !Array.isArray(metric) && metric.source_column
        ? metric.source_column
        : fallbackColumn
);

const getStructuredOpportunityDetail = ({
    metric,
    label,
    fallbackColumn,
    copy,
    locale,
    formatCellValue,
}) => {
    if (metric === undefined) return null;
    const value = getStructuredOpportunityMetricValue(metric);
    const numericValue = parseNumber(value);
    const isUnavailable = numericValue === null;
    const column = getStructuredOpportunityMetricColumn(metric, fallbackColumn);
    return {
        label,
        column,
        value: numericValue,
        formattedValue: isUnavailable
            ? copy.impact.notAvailable
            : formatCellValue(column, numericValue, { copy, locale }),
        isUnavailable,
    };
};

const getStructuredOperatingVehicleBasisDetail = ({
    metric,
    label,
    copy,
    locale,
    formatCellValue,
}) => {
    if (metric === undefined) return null;
    const value = getStructuredOpportunityMetricValue(metric);
    const numericValue = parseNumber(value);
    const isUnavailable = numericValue === null;
    return {
        label,
        value: numericValue,
        formattedValue: isUnavailable
            ? copy.impact.notAvailable
            : formatCellValue(OPERATING_VEHICLES_COLUMN, numericValue, { copy, locale }),
        isUnavailable,
    };
};

const hasStructuredDetailValue = (detail) => (
    detail?.value !== null && detail?.value !== undefined
);

const getStructuredOperatingOpportunitySummary = ({
    allVehiclesResult,
    copy,
    locale,
    formatCellValue,
}) => {
    const summary = allVehiclesResult?.payload?.impact_summary?.opportunity_savings
        || allVehiclesResult?.impact_summary?.opportunity_savings;
    if (!summary || typeof summary !== 'object') return null;

    const actualSavings = getStructuredOpportunityDetail({
        metric: summary.actual_savings,
        label: copy.impact.actualSavings,
        fallbackColumn: ACTUAL_SAVINGS_COLUMN,
        copy,
        locale,
        formatCellValue,
    });
    const maxOperatingSavings = getStructuredOpportunityDetail({
        metric: summary.max_operating_savings,
        label: copy.impact.maxOperatingSavings,
        fallbackColumn: MAX_OPERATING_SAVINGS_COLUMN,
        copy,
        locale,
        formatCellValue,
    });
    let additionalOpportunity = getStructuredOpportunityDetail({
        metric: summary.additional_opportunity,
        label: copy.impact.additionalOpportunity,
        fallbackColumn: MAX_OPERATING_SAVINGS_COLUMN,
        copy,
        locale,
        formatCellValue,
    });
    const operatingVehiclesBasis = getStructuredOperatingVehicleBasisDetail({
        metric: summary.operating_vehicles_basis,
        label: copy.impact.operatingVehiclesBasis,
        copy,
        locale,
        formatCellValue,
    });
    const hasActualAndMax = hasStructuredDetailValue(actualSavings)
        && hasStructuredDetailValue(maxOperatingSavings);
    const isCalculable = Boolean(summary.is_calculable ?? hasActualAndMax)
        && hasActualAndMax;
    if (
        isCalculable
        && !hasStructuredDetailValue(additionalOpportunity)
        && hasStructuredDetailValue(actualSavings)
        && hasStructuredDetailValue(maxOperatingSavings)
    ) {
        const value = Math.max(0, maxOperatingSavings.value - actualSavings.value);
        additionalOpportunity = {
            label: copy.impact.additionalOpportunity,
            column: MAX_OPERATING_SAVINGS_COLUMN,
            value,
            formattedValue: formatCellValue(MAX_OPERATING_SAVINGS_COLUMN, value, { copy, locale }),
            isUnavailable: false,
        };
    }

    return {
        isCalculable,
        actualSavings,
        maxOperatingSavings,
        additionalOpportunity,
        operatingVehiclesBasis,
        unavailableMessage: isCalculable ? '' : copy.impact.opportunityUnavailable,
        unavailableReason: summary.unavailable_reason || '',
        provenance: summary.provenance || null,
    };
};

export const getOperatingOpportunitySummary = ({
    allVehiclesResult,
    copy = DEFAULT_OPPORTUNITY_COPY,
    locale,
    formatCellValue = fallbackFormatCellValue,
} = {}) => {
    const structuredSummary = getStructuredOperatingOpportunitySummary({
        allVehiclesResult,
        copy,
        locale,
        formatCellValue,
    });
    if (structuredSummary) return structuredSummary;

    const actualSavings = sumResultColumn(allVehiclesResult, ACTUAL_SAVINGS_COLUMN);
    const maxOperatingSavings = getMaxOperatingSavingsSummary(allVehiclesResult);
    if (!actualSavings) return null;

    const unavailableDetail = (label) => ({
        label,
        value: null,
        formattedValue: copy.impact.notAvailable,
        isUnavailable: true,
    });

    const actualSavingsDetail = {
        label: copy.impact.actualSavings,
        value: actualSavings.value,
        formattedValue: formatCellValue(actualSavings.column, actualSavings.value, { copy, locale }),
    };

    if (!maxOperatingSavings) {
        return {
            isCalculable: false,
            actualSavings: actualSavingsDetail,
            maxOperatingSavings: unavailableDetail(copy.impact.maxOperatingSavings),
            additionalOpportunity: unavailableDetail(copy.impact.additionalOpportunity),
            operatingVehiclesBasis: null,
            unavailableMessage: copy.impact.opportunityUnavailable,
        };
    }

    const additionalOpportunity = Math.max(0, maxOperatingSavings.value - actualSavings.value);
    const opportunityColumn = maxOperatingSavings.column;

    return {
        isCalculable: true,
        actualSavings: actualSavingsDetail,
        maxOperatingSavings: {
            label: copy.impact.maxOperatingSavings,
            value: maxOperatingSavings.value,
            formattedValue: formatCellValue(maxOperatingSavings.column, maxOperatingSavings.value, { copy, locale }),
        },
        additionalOpportunity: {
            label: copy.impact.additionalOpportunity,
            value: additionalOpportunity,
            formattedValue: formatCellValue(opportunityColumn, additionalOpportunity, { copy, locale }),
        },
        operatingVehiclesBasis: null,
        unavailableMessage: '',
    };
};
